import { create } from 'zustand';

/**
 * A single open dialog instance. Generic on purpose: `id` is a plain string and
 * `props` are `unknown` so this store carries NO feature imports (the typed
 * `openDialog`/registry live in `src/app/dialogRegistry.tsx`, the composition
 * root that may import feature dialogs). `open` drives the two-phase close — it
 * flips to `false` so the `Dialog` primitive plays its exit animation, then the
 * host removes the instance.
 */
interface OpenDialogInstance {
  instanceId: string;
  id: string;
  props: unknown;
  open: boolean;
}

/**
 * Registry-driven dialog state. Lives in a Zustand store (per the "stores live
 * in a `stores/` segment" rule), mirroring the toast surface: a single
 * `<DialogHost/>` renders the stack and the imperative `openDialog` façade
 * pushes onto it. A stack (not a single slot) so dialogs can nest.
 */
interface DialogState {
  dialogs: OpenDialogInstance[];
  push: (id: string, props: unknown) => string;
  dismissTop: (instanceId: string) => void;
  hide: (instanceId: string) => void;
  remove: (instanceId: string) => void;
}

export const useDialogStore = create<DialogState>((set) => {
  let instanceCounter = 0;

  function nextInstanceId(): string {
    instanceCounter += 1;
    return `dialog-${instanceCounter}`;
  }

  return {
    dialogs: [],
    push: (id, props) => {
      const instanceId = nextInstanceId();
      set((s) => ({ dialogs: [...s.dialogs, { instanceId, id, props, open: true }] }));
      return instanceId;
    },
    dismissTop: (instanceId) =>
      set((s) => {
        const top = s.dialogs[s.dialogs.length - 1];
        if (!top || top.instanceId !== instanceId || !top.open) return s;
        return {
          dialogs: s.dialogs.map((dialog) =>
            dialog.instanceId === instanceId ? { ...dialog, open: false } : dialog,
          ),
        };
      }),
    hide: (instanceId) =>
      set((s) => ({
        dialogs: s.dialogs.map((d) => (d.instanceId === instanceId ? { ...d, open: false } : d)),
      })),
    remove: (instanceId) =>
      set((s) => ({ dialogs: s.dialogs.filter((d) => d.instanceId !== instanceId) })),
  };
});
